import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { FaShieldAlt, FaServer, FaCode, FaArrowRight, FaCheck } from 'react-icons/fa';
import { defaultViewport, staggerContainer, staggerItem, getRevealProps } from '../motion/scrollMotion';
import './SecurityArchitecture.css';

const pillars = [
  {
    tag: 'Pillar 01',
    icon: <FaShieldAlt />,
    title: 'Cybersecurity',
    desc: 'Find weaknesses before attackers do, and stand up the monitoring to catch what gets through — delivered by CISSP, CEH, and OSCP certified professionals.',
    points: [
      'Penetration testing, red teaming & VAPT',
      'Cloud, application & network security',
      'Compliance — ISO 27001, NIST CSF 2.0, CIS',
      '24/7 SOC monitoring & incident response',
    ],
    href: '/cybersecurity',
    cta: 'Explore Cybersecurity',
  },
  {
    tag: 'Pillar 02',
    icon: <FaServer />,
    title: 'Managed IT',
    desc: 'IT operations run with cybersecurity principles embedded in every process — not generic helpdesk support bolted onto your business.',
    points: [
      'Endpoint & identity management',
      'Server, network & patch management',
      'Cloud infrastructure support',
      'Backup & ransomware-resilient DR',
    ],
    href: '/managed-it',
    cta: 'Explore Managed IT',
  },
  {
    tag: 'Pillar 03',
    icon: <FaCode />,
    title: 'Secure Software',
    desc: 'Custom and AI-powered applications engineered secure by design — a security-focused engineering partner, not just a dev shop.',
    points: [
      'Custom & AI application development',
      'Secure SDLC (SAST / DAST / SCA)',
      'API integration & automation',
      'Security dashboards & reporting',
    ],
    href: '/development',
    cta: 'Explore Development',
  },
];

const SecurityArchitecture = () => (
  <section className="pillars-section">
    <div className="pillars-container">
      <motion.header className="pillars-header" {...getRevealProps('fadeUp')}>
        <span className="pillars-eyebrow">What We Do</span>
        <h2 className="pillars-title">One Partner, Three Disciplines.</h2>
        <p className="pillars-sub">
          Security, IT operations, and software engineering under one roof — so the gaps that
          fragmented vendors leave behind never become your problem.
        </p>
      </motion.header>

      <motion.div
        className="pillars-grid"
        initial="hidden"
        whileInView="visible"
        viewport={defaultViewport}
        variants={{ ...staggerContainer, visible: { transition: { staggerChildren: 0.1 } } }}
      >
        {pillars.map((p) => (
          <motion.div className="pillar-card" key={p.title} variants={staggerItem}>
            <div className="pillar-card-top">
              <div className="pillar-icon">{p.icon}</div>
              <span className="pillar-tag">{p.tag}</span>
            </div>
            <h3 className="pillar-card-title">{p.title}</h3>
            <p className="pillar-card-desc">{p.desc}</p>
            <ul className="pillar-points">
              {p.points.map((pt) => (
                <li key={pt}><FaCheck /> {pt}</li>
              ))}
            </ul>
            <Link to={p.href} className="pillar-link">
              {p.cta} <FaArrowRight />
            </Link>
          </motion.div>
        ))}
      </motion.div>
    </div>
  </section>
);

export default SecurityArchitecture;
